import React, { useMemo } from 'react';
import type { IaHandoffBin, Lead } from '../../../types';
import { countLeadsByHandoffBin, groupLeadsByHandoffBin } from '../handoffBinHelpers';
import { leadInitials } from '../funilIaStage';
import { HandoffBinIcon } from './HandoffBinIcon';

/** Cores e copy do protótipo GHI-FunilAI (5 bins com optout). */
const BIN_THEME: {
  key: IaHandoffBin;
  color: string;
  soft: string;
  line: string;
}[] = [
  { key: 'human', color: '#F43F5E', soft: '#FFF1F2', line: '#FECDD3' },
  { key: 'inactive', color: '#F59E0B', soft: '#FFFBEB', line: '#FDE68A' },
  { key: 'lowintent', color: '#64748B', soft: '#F1F5F9', line: '#CBD5E1' },
  { key: 'optout', color: '#0D9488', soft: '#F0FDFA', line: '#99F6E4' },
  { key: 'toxic', color: '#7C3AED', soft: '#F5F3FF', line: '#DDD6FE' },
];

// Slug `inactive` preservado para compat com `lead.iaConversationContext.handoffBin`
// já persistido. A lane consolida 2 fontes (cf. ADR-0019 split-crm-manager-from-sdr/design.md):
//   1. Leads sem resposta há 3 dias (cron `inactivityEvaluation`).
//   2. Leads que sinalizaram `remarketing` no CRM Manager (interesse futuro
//      declarado — "ano que vem", "depois", "me liga em N meses").
// Operacionalmente são o mesmo bucket: "lead parqueado, volta depois".
const BIN_COPY: Record<IaHandoffBin, { label: string; sub: string }> = {
  human: { label: 'Atendimento humano', sub: 'Escalação ativa' },
  inactive: { label: 'Inativos / Remarketing', sub: 'Sem resposta ou volta depois' },
  lowintent: { label: 'Sem interesse', sub: 'Não-acionável' },
  optout: { label: 'Opt-out comercial', sub: 'Não contactar mais' },
  toxic: { label: 'Tóxico', sub: 'Conteúdo abusivo' },
};

function initialsFor(lead: Lead): string {
  return leadInitials(lead.nome || lead.telefone || '');
}

export interface HandoffBinsColumnProps {
  leads: readonly Lead[];
  /** Refs para alvo de animação (cartão completo) — chave = handoffBin. */
  binRefs: Record<IaHandoffBin, React.RefObject<HTMLDivElement | null>>;
}

export const HandoffBinsColumn: React.FC<HandoffBinsColumnProps> = ({ leads, binRefs }) => {
  const counts = useMemo(() => countLeadsByHandoffBin(leads), [leads]);
  const grouped = useMemo(() => groupLeadsByHandoffBin(leads), [leads]);

  return (
    <aside className="funil-ia-handoff-bins" aria-label="Colectores de handoff">
      <div className="funil-ia-handoff-bins-title">Handoff</div>
      <ul className="funil-ia-handoff-bins-list" role="list">
        {BIN_THEME.map(({ key, color, soft, line }) => {
          const { label, sub } = BIN_COPY[key];
          const n = counts[key];
          const stack = grouped[key];
          return (
            <li key={key} className="funil-ia-handoff-bin" role="listitem">
              <div
                ref={binRefs[key] as React.LegacyRef<HTMLDivElement>}
                className="funil-ia-handoff-bin-target"
                data-bin={key}
                style={
                  {
                    '--bin': color,
                    '--bin-soft': soft,
                    '--bin-line': line,
                  } as React.CSSProperties
                }
              >
                <div className="funil-ia-handoff-bin-mouth" aria-hidden />
                <div className="funil-ia-handoff-bin-body">
                  <div className="funil-ia-handoff-bin-stack" aria-hidden>
                    {stack.slice(0, 8).map((l, idx) => {
                      const col = idx % 6;
                      const row = Math.floor(idx / 6);
                      const avatar = 18;
                      const rowGap = 4;
                      const baseBottom = 2;
                      return (
                        <span
                          key={l.leadId}
                          className="funil-ia-handoff-bin-avatar"
                          style={{
                            left: 8 + col * 14,
                            bottom: baseBottom + row * (avatar + rowGap),
                            background: `linear-gradient(145deg, ${color}dd, ${color})`,
                            animationDelay: `${idx * 40}ms`,
                          }}
                          title={l.nome || l.telefone}
                        >
                          {initialsFor(l)}
                        </span>
                      );
                    })}
                  </div>
                  <div className="funil-ia-handoff-bin-icon" style={{ color }}>
                    <HandoffBinIcon kind={key} />
                  </div>
                  <div className="funil-ia-handoff-bin-text">
                    <div className="funil-ia-handoff-bin-copy">
                      <div className="funil-ia-handoff-bin-label">{label}</div>
                      <div className="funil-ia-handoff-bin-sub">{sub}</div>
                    </div>
                    <div className="funil-ia-handoff-bin-count" style={{ color }}>
                      {n}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};
